import { create } from 'zustand';
import { ACCESS_TOKEN_KEY, USER_KEY } from '../api/client';
import type { AuthResponse, User } from '../types';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  /** Set once `initAuth` has read localStorage (avoids a flash of the login page). */
  hydrated: boolean;
  setUser: (user: User | null) => void;
  /** Persist tokens + user after login/register. */
  setSession: (auth: Pick<AuthResponse, 'accessToken' | 'user'> & Partial<AuthResponse>) => void;
  logout: () => void;
  initAuth: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  hydrated: false,

  setUser: (user) => {
    if (user) {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      set({ user, isAuthenticated: true });
    } else {
      localStorage.removeItem(USER_KEY);
      set({ user: null, isAuthenticated: false });
    }
  },

  setSession: ({ accessToken, refreshToken, user }) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    set({ user, isAuthenticated: true, hydrated: true });
  },

  logout: () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem('refresh_token');
    localStorage.removeItem(USER_KEY);
    set({ user: null, isAuthenticated: false });
  },

  initAuth: () => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    const userStr = localStorage.getItem(USER_KEY);
    if (token && userStr) {
      try {
        const user = JSON.parse(userStr) as User;
        set({ user, isAuthenticated: true, hydrated: true });
        return;
      } catch {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
      }
    }
    set({ user: null, isAuthenticated: false, hydrated: true });
  },
}));

// Hydrate synchronously on module load so routes render the right branch on first paint.
useAuthStore.getState().initAuth();
